import { useEffect, useRef, useState } from 'react';
import {
  Button, Empty, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import { DatabaseOutlined, DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import AppCard from '../../../components/cards/AppCard';
import {
  listModuleRecords, createModuleRecordGeneric, updateModuleRecordGeneric, deleteModuleRecordGeneric,
  getLocationMasterOptions,
} from '../../../services/adminApi';
import '../../../styles/FormGroupsSection.css';

const { Text } = Typography;
const idOf = (record) => record?._id ?? record?.id;
const MASTER_COLLECTION_MODULE = 'masters';

function scopePayload() {
  return {
    tenantId: localStorage.getItem('tenantId') || '',
    businessId: localStorage.getItem('businessId') || '',
    businessUnitId: localStorage.getItem('businessUnitId') || '',
  };
}

function oidValue(value) {
  if (value && typeof value === 'object') return value.$oid ?? value.oid ?? value.id ?? '';
  return value ?? '';
}

function recordScopeValue(record, keys) {
  return keys.map((key) => oidValue(record?.[key])).find((value) => value !== undefined && value !== null && value !== '') ?? '';
}

function recordMatchesScope(record, scope) {
  const pairs = [
    [scope.tenantId, ['tid', 'tenantId']],
    [scope.businessId, ['bid', 'businessId']],
    [scope.businessUnitId, ['buid', 'businessUnitId']],
  ];
  return pairs.every(([expected, keys]) => {
    if (!expected) return true;
    const actual = String(recordScopeValue(record, keys));
    return !actual || actual === String(expected);
  });
}

function masterFeeds(record) {
  return Array.isArray(record?.masterFeeds) ? record.masterFeeds : [];
}

function nextFeedUniqueId(feeds) {
  const max = feeds.reduce((highest, feed) => Math.max(highest, Number(feed?.uniqueId) || 0), 0);
  return max + 1;
}

// ── tax value / history helpers ──────────────────────────────────────────────
function taxHistoryList(record) {
  return Array.isArray(record?.taxHistory) ? record.taxHistory : [];
}

function currentTaxValue(record) {
  const root = record?.taxValue;
  if (root !== undefined && root !== null && root !== '') return Number(root);
  const last = taxHistoryList(record).at(-1);
  const value = last?.taxValue ?? last?.value;
  return value !== undefined && value !== null && value !== '' ? Number(value) : undefined;
}

function formatDateTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
}

function latestTaxUpdatedAt(record) {
  const last = taxHistoryList(record).at(-1);
  return last?.updatedAt ?? last?.timestamp ?? record?.updatedAt;
}

// ── Location + Tax editor ────────────────────────────────────────────────────
// Location is chosen from the locationMasters collection. Once a record exists,
// the location is locked and only the tax is editable (every tax change is kept
// in taxHistory with its timestamp).
function LocationEditor({ open, record, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);
  const isEdit = Boolean(record);

  useEffect(() => {
    if (!open) return;
    queueMicrotask(() => {
      form.resetFields();
      form.setFieldsValue({ name: record?.name ?? undefined, taxValue: currentTaxValue(record) });
      // Seed the select with the current location so it renders on edit without a search.
      setOptions(record?.name ? [{ label: record.name, value: record.name }] : []);
    });
  }, [open, record, form]);

  function runSearch(search) {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!search || search.trim().length < 2) { setOptions([]); return; }
    setSearching(true);
    searchTimer.current = setTimeout(() => {
      getLocationMasterOptions(search.trim())
        .then(setOptions)
        .catch(() => setOptions([]))
        .finally(() => setSearching(false));
    }, 350);
  }

  async function save() {
    const values = await form.validateFields();
    const taxValue = Number(values.taxValue);
    const payload = {
      name: values.name,
      // Root taxValue stays the current value — configured submission lookups
      // read locations.taxValue directly. The gateway appends {taxValue,
      // updatedAt, updatedBy} to taxHistory (config-driven appendHistory).
      taxValue,
      taxHistory: { taxValue },
    };
    setSaving(true);
    try {
      if (isEdit) await updateModuleRecordGeneric('locations', idOf(record), payload);
      else await createModuleRecordGeneric('locations', payload);
      message.success(isEdit ? 'Tax updated' : 'Location and tax created');
      onSaved();
    } catch (error) {
      message.error(error?.message || 'Could not save location and tax');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={save}
      confirmLoading={saving}
      destroyOnHidden
      title={isEdit ? `Edit Tax — ${record?.name ?? ''}` : 'New Location'}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Location"
          rules={[{ required: true, message: 'Location is required' }]}
          tooltip={isEdit ? 'Location cannot be changed once created — only the tax is editable.' : 'Search and pick from the location master.'}
        >
          <Select
            showSearch
            disabled={isEdit}
            placeholder="Type at least 2 characters to search…"
            filterOption={false}
            onSearch={runSearch}
            notFoundContent={searching ? 'Searching…' : null}
            options={options}
          />
        </Form.Item>

        <Form.Item
          name="taxValue"
          label="Tax"
          rules={[
            { required: true, message: 'Tax is required' },
            { type: 'number', min: 0, max: 100, message: 'Tax must be between 0 and 100' },
          ]}
        >
          <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.01} suffix="%" placeholder="8.875" autoFocus={isEdit} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Tax timeline (expanded row) ──────────────────────────────────────────────
function TaxTimeline({ record }) {
  const history = [...taxHistoryList(record)].reverse(); // newest first
  if (!history.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No tax history yet" />;
  return (
    <Space direction="vertical" size={4} style={{ padding: '4px 8px' }}>
      {history.map((entry, index) => (
        <Space key={index} size={10}>
          <Tag color={index === 0 ? 'green' : 'default'} style={{ margin: 0 }}>
            {entry.taxValue ?? entry.value}%
          </Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>{formatDateTime(entry.updatedAt ?? entry.timestamp)}</Text>
          {index === 0 && <Text type="secondary" style={{ fontSize: 12 }}>(current)</Text>}
        </Space>
      ))}
    </Space>
  );
}

function LocationsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  function load() {
    setLoading(true);
    listModuleRecords('locations')
      .then(({ items: data }) => setItems(data))
      .catch((error) => message.error(error?.message || 'Could not load locations'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { queueMicrotask(load); }, []);

  async function remove(id) {
    try {
      await deleteModuleRecordGeneric('locations', id);
      message.success('Location deleted');
      load();
    } catch (error) {
      message.error(error?.message || 'Could not delete location');
    }
  }

  const columns = [
    { title: 'Location', dataIndex: 'name', key: 'name' },
    {
      title: 'Tax',
      key: 'taxValue',
      width: 100,
      render: (_, record) => {
        const value = currentTaxValue(record);
        return Number.isFinite(value) ? <Tag color="blue">{value}%</Tag> : '—';
      },
    },
    { title: 'Tax updated', key: 'taxUpdatedAt', render: (_, record) => formatDateTime(latestTaxUpdatedAt(record)) },
    { title: 'Created', key: 'createdAt', responsive: ['lg'], render: (_, record) => formatDateTime(record?.createdAt) },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit tax">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => { setEditing(record); setEditorOpen(true); }} />
          </Tooltip>
          <Popconfirm title="Delete this location?" onConfirm={() => remove(idOf(record))}>
            <Tooltip title="Delete"><Button type="text" size="small" danger icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="fg-section admin-config-section">
      <AppCard
        title={`${items.length} location${items.length === 1 ? '' : 's'}`}
        extra={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load}>Reload</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); setEditorOpen(true); }}>
              New location
            </Button>
          </Space>
        )}
      >
        <Table
          rowKey={idOf}
          columns={columns}
          dataSource={items}
          loading={loading}
          size="small"
          pagination={false}
          className="fg-table"
          expandable={{
            expandedRowRender: (record) => <TaxTimeline record={record} />,
            rowExpandable: (record) => taxHistoryList(record).length > 0,
          }}
          locale={{ emptyText: 'No locations yet. Click "New location" to add one.' }}
        />
      </AppCard>
      <LocationEditor
        open={editorOpen}
        record={editing}
        onClose={() => setEditorOpen(false)}
        onSaved={() => { setEditorOpen(false); load(); }}
      />
    </div>
  );
}

function MasterCollectionEditor({ open, record, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(record);
  const watchedMasterName = Form.useWatch('masterName', form);
  const watchedFeeds = Form.useWatch('masterFeeds', form) ?? [];
  const isClientSubmissionStatusMaster = ['clientsubmissionstatus', 'clientsubmission', 'clientsubmissions']
    .includes(String(watchedMasterName ?? record?.masterName ?? '').trim().toLowerCase());
  const statusFeedOptions = watchedFeeds
    .map((feed) => ({
      label: String(feed?.name ?? '').trim() || `Status ${feed?.uniqueId ?? ''}`,
      value: Number(feed?.uniqueId),
    }))
    .filter((option) => Number.isInteger(option.value) && option.value > 0);

  function setSingleInitialStatus(rowIndex, enabled) {
    if (!enabled) {
      form.setFieldValue(['masterFeeds', rowIndex, 'isInitialStatus'], false);
      return;
    }

    const feeds = form.getFieldValue('masterFeeds') ?? [];
    feeds.forEach((_, index) => {
      form.setFieldValue(['masterFeeds', index, 'isInitialStatus'], index === rowIndex);
    });
  }

  function setTerminalStatus(rowIndex, enabled) {
    form.setFieldValue(['masterFeeds', rowIndex, 'isTerminalStatus'], enabled);
    if (enabled) {
      form.setFieldValue(['masterFeeds', rowIndex, 'allowedNextStatusIds'], []);
    }
  }

  useEffect(() => {
    if (!open) return;
    queueMicrotask(() => {
      form.resetFields();

      const masterName = record?.masterName ?? '';
      const clientStatusMaster = ['clientsubmissionstatus', 'clientsubmission', 'clientsubmissions']
        .includes(String(masterName).trim().toLowerCase());
      const existingFeeds = masterFeeds(record);
      let editorFeeds = existingFeeds.length
        ? existingFeeds.map((feed) => ({ ...feed }))
        : [{ uniqueId: 1, name: '', type: 'neutral', status: true, isdeleted: false }];

      // Backward compatibility for existing clientSubmissionStatus masters that
      // were created before Initial/Terminal/transition fields were introduced.
      // If there is no active Initial status, make the first active feed Initial
      // so the record can be edited/saved without manual migration first.
      if (clientStatusMaster) {
        const hasActiveInitial = editorFeeds.some((feed) => (
          feed?.status !== false
          && feed?.isdeleted !== true
          && feed?.isInitialStatus === true
        ));

        if (!hasActiveInitial) {
          const firstActiveIndex = editorFeeds.findIndex((feed) => (
            feed?.status !== false && feed?.isdeleted !== true
          ));
          if (firstActiveIndex >= 0) {
            editorFeeds = editorFeeds.map((feed, index) => ({
              ...feed,
              isInitialStatus: index === firstActiveIndex,
              isTerminalStatus: feed?.isTerminalStatus === true,
              allowedNextStatusIds: Array.isArray(feed?.allowedNextStatusIds)
                ? feed.allowedNextStatusIds
                : [],
            }));
          }
        }
      }

      form.setFieldsValue({
        masterName,
        schemaVersion: record?.schemaVersion ?? 1,
        masterFeeds: editorFeeds,
      });
    });
  }, [open, record, form]);

  useEffect(() => {
    if (!open || !isClientSubmissionStatusMaster) return;
    const currentVersion = Number(form.getFieldValue('schemaVersion')) || 1;
    if (currentVersion < 2) form.setFieldValue('schemaVersion', 2);
  }, [open, isClientSubmissionStatusMaster, form]);

  async function save() {
    const values = await form.validateFields();
    const scope = scopePayload();
    const clientStatusMaster = ['clientsubmissionstatus', 'clientsubmission', 'clientsubmissions']
      .includes(String(values.masterName ?? '').trim().toLowerCase());
    const feeds = (values.masterFeeds ?? [])
      .map((feed, index) => {
        const uniqueId = Number(feed.uniqueId) || index + 1;
        const isTerminalStatus = clientStatusMaster && feed.isTerminalStatus === true;
        const allowedNextStatusIds = clientStatusMaster && !isTerminalStatus
          ? [...new Set((feed.allowedNextStatusIds ?? [])
            .map(Number)
            .filter((id) => Number.isInteger(id) && id > 0 && id !== uniqueId))]
          : [];
        return {
          uniqueId,
          name: String(feed.name ?? '').trim(),
          type: String(feed.type ?? '').trim(),
          status: feed.status !== false,
          ...(clientStatusMaster ? {
            isInitialStatus: feed.isInitialStatus === true,
            isTerminalStatus,
            allowedNextStatusIds,
          } : {}),
          isdeleted: feed.isdeleted === true,
        };
      })
      .filter((feed) => feed.name);

    if (clientStatusMaster) {
      let activeInitialStatuses = feeds.filter((feed) => feed.status && !feed.isdeleted && feed.isInitialStatus);

      // Legacy-safe fallback: older master records do not have isInitialStatus.
      // When none is selected, promote the first active status automatically.
      if (activeInitialStatuses.length === 0) {
        const firstActiveIndex = feeds.findIndex((feed) => feed.status && !feed.isdeleted);
        if (firstActiveIndex >= 0) {
          feeds[firstActiveIndex] = {
            ...feeds[firstActiveIndex],
            isInitialStatus: true,
          };
          activeInitialStatuses = [feeds[firstActiveIndex]];
          form.setFieldValue(['masterFeeds', firstActiveIndex, 'isInitialStatus'], true);
        }
      }

      if (activeInitialStatuses.length !== 1) {
        message.error('Client Submission Status must have exactly one active Initial Status.');
        return;
      }
    }
    const payload = {
      ...scope,
      tid: scope.tenantId,
      bid: scope.businessId,
      buid: scope.businessUnitId,
      schemaVersion: clientStatusMaster ? Math.max(2, Number(values.schemaVersion) || 2) : (Number(values.schemaVersion) || 1),
      masterName: values.masterName.trim(),
      masterFeeds: feeds,
    };

    setSaving(true);
    try {
      if (isEdit) await updateModuleRecordGeneric(MASTER_COLLECTION_MODULE, idOf(record), payload);
      else await createModuleRecordGeneric(MASTER_COLLECTION_MODULE, payload);
      message.success(isEdit ? 'Master value updated' : 'Master value created');
      onSaved();
    } catch (error) {
      message.error(error?.message || 'Could not save master value');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={save}
      confirmLoading={saving}
      destroyOnHidden
      title={isEdit ? 'Edit Master Feed' : 'New Master Feed'}
      width={1040}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="masterName" label="Master Name" rules={[{ required: true, message: 'Master name is required' }]}>
          <Input placeholder="blacklistType" autoFocus={!isEdit} disabled={isEdit} />
        </Form.Item>
        <Form.Item name="schemaVersion" label="Schema Version">
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.List name="masterFeeds">
          {(fields, { add, remove }) => (
            <Space direction="vertical" style={{ width: '100%' }} size={10}>
              {fields.map(({ key, name }) => (
                <Space key={key} align="baseline" wrap>
                  <Form.Item name={[name, 'uniqueId']} label="ID" rules={[{ required: true, message: 'ID required' }]}>
                    <InputNumber min={1} style={{ width: 90 }} />
                  </Form.Item>
                  <Form.Item name={[name, 'name']} label="Name" rules={[{ required: true, message: 'Name required' }]}>
                    <Input placeholder="Dropdown label" style={{ width: 280 }} />
                  </Form.Item>
                  <Form.Item name={[name, 'type']} label="Type">
                    <Select
                      allowClear
                      placeholder="Select type"
                      style={{ width: 120 }}
                      options={[
                        { label: 'Positive', value: 'positive' },
                        { label: 'Neutral', value: 'neutral' },
                        { label: 'Negative', value: 'negative' },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item name={[name, 'status']} label="Status" initialValue>
                    <Select
                      style={{ width: 110 }}
                      options={[{ label: 'Active', value: true }, { label: 'Inactive', value: false }]}
                    />
                  </Form.Item>
                  <Form.Item name={[name, 'isdeleted']} label="Deleted" initialValue={false}>
                    <Select
                      style={{ width: 110 }}
                      options={[{ label: 'No', value: false }, { label: 'Yes', value: true }]}
                    />
                  </Form.Item>
                  {isClientSubmissionStatusMaster && (
                    <>
                      <Form.Item
                        name={[name, 'isInitialStatus']}
                        label="Initial"
                        initialValue={false}
                        tooltip="The initial status is the only option shown before a candidate has a client submission status. Only one active initial status is allowed."
                      >
                        <Select
                          style={{ width: 100 }}
                          onChange={(value) => setSingleInitialStatus(name, value === true)}
                          options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]}
                        />
                      </Form.Item>
                      <Form.Item
                        name={[name, 'isTerminalStatus']}
                        label="End Status"
                        initialValue={false}
                        tooltip="End Status stops the configured client submission flow. Once reached, Change Status is hidden and no next statuses are allowed."
                      >
                        <Select
                          style={{ width: 105 }}
                          onChange={(value) => setTerminalStatus(name, value === true)}
                          options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]}
                        />
                      </Form.Item>
                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, next) => (
                          prev?.masterFeeds?.[name]?.isTerminalStatus !== next?.masterFeeds?.[name]?.isTerminalStatus
                          || prev?.masterFeeds?.[name]?.uniqueId !== next?.masterFeeds?.[name]?.uniqueId
                          || prev?.masterFeeds !== next?.masterFeeds
                        )}
                      >
                        {({ getFieldValue }) => {
                          const terminal = getFieldValue(['masterFeeds', name, 'isTerminalStatus']) === true;
                          const currentId = Number(getFieldValue(['masterFeeds', name, 'uniqueId']));
                          return (
                            <Form.Item
                              name={[name, 'allowedNextStatusIds']}
                              label="Allowed Next Statuses"
                              tooltip={terminal ? 'End Status cannot move to another status.' : 'Only these statuses will appear after the current status is saved.'}
                            >
                              <Select
                                mode="multiple"
                                allowClear
                                disabled={terminal}
                                placeholder={terminal ? 'End status' : 'Select next statuses'}
                                style={{ minWidth: 280 }}
                                options={statusFeedOptions.filter((option) => option.value !== currentId)}
                              />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </>
                  )}
                  <Tooltip title="Remove row">
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                  </Tooltip>
                </Space>
              ))}
              <Button
                icon={<PlusOutlined />}
                onClick={() => add({
                  uniqueId: nextFeedUniqueId(form.getFieldValue('masterFeeds') ?? []),
                  name: '',
                  type: 'neutral',
                  status: true,
                  isdeleted: false,
                  ...(isClientSubmissionStatusMaster ? {
                    isInitialStatus: false,
                    isTerminalStatus: false,
                    allowedNextStatusIds: [],
                  } : {}),
                })}
              >
                Add feed value
              </Button>
            </Space>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
}

function MasterCollectionsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [masterName, setMasterName] = useState('');

  function load() {
    const scope = scopePayload();
    setLoading(true);
    listModuleRecords(MASTER_COLLECTION_MODULE, { limit: 500 })
      .then(({ items: data }) => {
        const scoped = data.filter((record) => recordMatchesScope(record, scope));
        setItems(scoped);
      })
      .catch((error) => message.error(error?.message || 'Could not load master values'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { queueMicrotask(load); }, []);

  async function remove(id) {
    try {
      await deleteModuleRecordGeneric(MASTER_COLLECTION_MODULE, id);
      message.success('Master value deleted');
      load();
    } catch (error) {
      message.error(error?.message || 'Could not delete master value');
    }
  }

  const filteredItems = masterName
    ? items.filter((record) => String(record?.masterName ?? '').toLowerCase() === masterName.toLowerCase())
    : items;
  const masterOptions = [...new Set(items.map((record) => record?.masterName).filter(Boolean))]
    .sort((a, b) => String(a).localeCompare(String(b)))
    .map((value) => ({ label: value, value }));

  const columns = [
    { title: 'Master Name', dataIndex: 'masterName', key: 'masterName', width: 180 },
    {
      title: 'Feed Values',
      key: 'masterFeeds',
      render: (_, record) => (
        <Space wrap>
          {masterFeeds(record).filter((feed) => !feed.isdeleted).map((feed) => (
            <Tag key={feed.uniqueId ?? feed.name} color={feed.status === false ? 'default' : 'blue'}>
              {feed.name}
            </Tag>
          ))}
        </Space>
      ),
    },
    { title: 'Count', key: 'count', width: 90, render: (_, record) => masterFeeds(record).filter((feed) => !feed.isdeleted).length },
    { title: 'Updated', key: 'updatedAt', responsive: ['lg'], render: (_, record) => formatDateTime(record?.updatedAt) },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => { setEditing(record); setEditorOpen(true); }} />
          </Tooltip>
          <Popconfirm title="Delete this master?" onConfirm={() => remove(idOf(record))}>
            <Tooltip title="Delete"><Button type="text" size="small" danger icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="fg-section admin-config-section">
      <AppCard
        title={`${filteredItems.length} master${filteredItems.length === 1 ? '' : 's'}`}
        extra={(
          <Space wrap>
            <Select
              allowClear
              showSearch
              placeholder="Filter master name"
              value={masterName || undefined}
              options={masterOptions}
              onChange={(value) => setMasterName(value ?? '')}
              style={{ minWidth: 220 }}
            />
            <Button icon={<ReloadOutlined />} onClick={load}>Reload</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); setEditorOpen(true); }}>
              New master
            </Button>
          </Space>
        )}
      >
        <Table
          rowKey={idOf}
          columns={columns}
          dataSource={filteredItems}
          loading={loading}
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          className="fg-table"
          locale={{ emptyText: 'No masters yet. Click "New master" to add one.' }}
        />
      </AppCard>
      <MasterCollectionEditor
        open={editorOpen}
        record={editing}
        onClose={() => setEditorOpen(false)}
        onSaved={() => { setEditorOpen(false); load(); }}
      />
    </div>
  );
}

export default function MastersSection() {
  return (
    <div className="fg-section">
      <AppCard icon={<DatabaseOutlined />} title="Master Collections">
        <div className="admin-accent-bar" />
        <Text type="secondary" style={{ fontSize: 13 }}>
          Create, update, and delete master documents, then customize multiple dropdown values inside masterFeeds.
          Records are saved with the current tenant, business, business unit, and master name.
        </Text>
      </AppCard>
      <MasterCollectionsPanel />
      <AppCard icon={<DatabaseOutlined />} title="Location Tax Master">
        <div className="admin-accent-bar" />
        <Text type="secondary" style={{ fontSize: 13 }}>
          Pick a location from the master list and set its tax. Once added, only the tax can be changed — every
          change is kept in a timeline with its timestamp. Expand a row to see the full tax history.
        </Text>
      </AppCard>
      <LocationsPanel />
    </div>
  );
}
